import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  CircularProgress,
  Button,
} from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { colors, fonts } from "../../../theme/theme";

const clamp = (n) => Math.max(0, Math.min(100, n));

// Parse "50% 30%" -> {x:50, y:30}. Default framing = horizontally centered, near top.
const parsePos = (str) => {
  if (typeof str === "string" && str.includes("%")) {
    const [x, y] = str.split(/\s+/).map((p) => parseFloat(p));
    if (!Number.isNaN(x) && !Number.isNaN(y)) return { x: clamp(x), y: clamp(y) };
  }
  return { x: 50, y: 20 };
};

// Square preview (matches the 1:1 photo shown on the Nirva site cards/carousels).
// The image is object-fit: cover and the practitioner drags to choose which part
// is visible — the object-position is saved; the original photo is never cropped.
const AvatarEditorModal = ({
  open,
  onClose,
  avatar,
  position,
  uploading,
  onReplace,
  onPositionChange,
}) => {
  const [pos, setPos] = useState(parsePos(position));
  const boxRef = useRef(null);
  const dragRef = useRef(null);
  const fileRef = useRef(null);

  // Re-sync when opening or when the stored position/avatar changes.
  useEffect(() => {
    if (open) setPos(parsePos(position));
  }, [open, position]);

  const onPointerDown = (e) => {
    if (!avatar) return;
    const rect = boxRef.current.getBoundingClientRect();
    dragRef.current = { startX: e.clientX, startY: e.clientY, ...pos, w: rect.width, h: rect.height };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    // Dragging the photo down reveals its top => object-position moves toward 0.
    const dx = ((e.clientX - d.startX) / d.w) * 100;
    const dy = ((e.clientY - d.startY) / d.h) * 100;
    setPos({ x: clamp(d.x - dx), y: clamp(d.y - dy) });
  };

  const onPointerUp = () => {
    dragRef.current = null;
  };

  const handleSave = () => {
    onPositionChange(`${Math.round(pos.x)}% ${Math.round(pos.y)}%`);
    onClose();
  };

  const objectPosition = `${pos.x}% ${pos.y}%`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontFamily: fonts.gotham, color: colors.primary }}>
        Profile Photo
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
          <Box
            ref={boxRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            sx={{
              position: "relative",
              width: 280,
              height: 280,
              borderRadius: "16px",
              overflow: "hidden",
              backgroundColor: colors.headerBg,
              cursor: avatar ? "grab" : "default",
              "&:active": { cursor: avatar ? "grabbing" : "default" },
              userSelect: "none",
            }}
          >
            {avatar ? (
              <img
                src={avatar}
                alt="Profile"
                draggable={false}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition,
                  pointerEvents: "none",
                }}
              />
            ) : (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: colors.textLight,
                  fontFamily: fonts.quicksand,
                }}
              >
                No photo yet
              </Box>
            )}
            {uploading && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255,255,255,0.6)",
                }}
              >
                <CircularProgress sx={{ color: colors.secondary }} />
              </Box>
            )}
          </Box>
          {avatar && (
            <Typography sx={{ fontFamily: fonts.quicksand, fontSize: 13, color: colors.textLight }}>
              Drag the photo to choose what shows on the Nirva site.
            </Typography>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onReplace(f);
              e.target.value = "";
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          variant="outlined"
          startIcon={<PhotoCameraIcon />}
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
          sx={{
            whiteSpace: "nowrap",
            textTransform: "none",
            fontFamily: fonts.quicksand,
            borderColor: colors.secondary,
            color: colors.secondary,
            "&:hover": { borderColor: colors.secondary, backgroundColor: colors.hoverBg },
          }}
        >
          {uploading ? "Uploading..." : avatar ? "Replace Photo" : "Upload Photo"}
        </Button>
        <Button
          variant="contained"
          disabled={!avatar || uploading}
          onClick={handleSave}
          sx={{
            whiteSpace: "nowrap",
            textTransform: "none",
            fontFamily: fonts.quicksand,
            backgroundColor: colors.secondary,
            "&:hover": { backgroundColor: colors.button?.primaryHover || "#1f2e3e" },
          }}
        >
          Save Position
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AvatarEditorModal;
